import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { EntryPassModel } from 'shared/models';
import { toDate } from 'shared/utilities';
import { UiIcon, UiModal, UiModalContent } from 'shared/uikit-new';
import { COLORS } from 'shared/constants';
import { useObservable, useOnClickOutside } from 'shared/hooks';

import './styles.scss';

import image from './assets/image.svg';

type PropsType = {
    entryPass: EntryPassModel,
    onCopy?: (entryPass: EntryPassModel) => void
};

export const VmEntryPass = observer(function VmEntryPass({
    entryPass,
    onCopy = () => undefined,
}: PropsType) {
    const ref = useRef<HTMLDivElement>(null);
    const store = useObservable({
        isOpened: false,
        isActive: false,
        isTouching: false,
        touchStart: 0,
        touchScroll: 0,
        longPressTimeout: undefined as undefined | NodeJS.Timeout,
    });

    useOnClickOutside(ref, () => {
        store.set('isActive', false);
    });

    const handleTouchStart = (touchEvent: React.TouchEvent<HTMLDivElement>) => {
        if (store.isTouching) {
            return;
        }

        if (touchEvent) {
            store.set('touchScroll', touchEvent.nativeEvent.view?.pageYOffset || 0);
        }

        store.set('isTouching', true);
        store.set('touchStart', Date.now());
        store.set('longPressTimeout', setTimeout(() => store.set('isActive', true), 700));
    };

    const handleTouchEnd = (touchEvent: React.TouchEvent<HTMLDivElement>) => {
        if (!store.isTouching) {
            return;
        }

        store.set('isTouching', false);

        if (touchEvent) {
            const touchScroll = touchEvent.nativeEvent.view?.pageYOffset || 0;
            if (Math.abs(store.touchScroll - touchScroll) > 30) {
                store.set('isActive', false);
                clearTimeout(store.longPressTimeout);
                store.set('longPressTimeout', undefined);
                return;
            }
        }

        if (Date.now() - store.touchStart < 400) {
            store.set('isActive', false);
            store.set('isOpened', true);
            clearTimeout(store.longPressTimeout);
            store.set('longPressTimeout', undefined);
        }
    };

    return (
        <>
            <div
                ref={ref}
                className={classnames('vm-entry-pass', {
                    'vm-entry-pass--active': store.isActive,
                })}
                onMouseDown={() => setTimeout(handleTouchStart, 20)}
                onMouseUp={() => setTimeout(handleTouchEnd, 60)}
                onTouchStart={(event) => setTimeout(() => handleTouchStart(event), 1)}
                onTouchEnd={(event) => setTimeout(() => handleTouchEnd(event), 30)}
            >
                <div
                    className="vm-entry-pass__image"
                    style={{ backgroundImage: `url(${image})` }}
                />
                <div className="vm-entry-pass__inner">
                    <div className="vm-entry-pass__header">
                        <span>{toDate(entryPass.entryAt).format('D MMMM YYYY')}; </span><span>{entryPass.visitor}</span>
                    </div>
                    <div className="vm-entry-pass__main">
                        <span>{entryPass.phone}; </span>
                        <span>{entryPass.transport}</span>
                    </div>
                </div>
                <div
                    className="vm-entry-pass__copy"
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onCopy(entryPass);
                    }}
                    onMouseDown={event => event.stopPropagation()}
                    onTouchStart={event => event.stopPropagation()}
                >
                    <UiIcon.CopyIcon size={20} color={COLORS._9EA2AA} />
                </div>
            </div>
            <UiModal isOpened={store.isOpened} onClose={() => store.set('isOpened', false)}>
                <UiModalContent>
                    <div className="vm-entry-pass-modal__title">
                        {entryPass.visitor}
                    </div>
                    <div className="vm-entry-pass-modal__properties">
                        <div className="vm-entry-pass-modal-property">
                            <div className="vm-entry-pass-modal-property__icon">
                                <UiIcon.Clock8 size={24} color={COLORS._858991} />
                            </div>
                            <div className="vm-entry-pass-modal-property__value">
                                {toDate(entryPass.entryAt).format('dddd, D MMMM YYYY')}
                            </div>
                        </div>
                        <div className="vm-entry-pass-modal-property">
                            <div className="vm-entry-pass-modal-property__icon">
                                <UiIcon.Phone size={24} color={COLORS._858991} />
                            </div>
                            <div className="vm-entry-pass-modal-property__value">
                                {entryPass.phone}
                            </div>
                        </div>
                        <div className="vm-entry-pass-modal-property">
                            <div className="vm-entry-pass-modal-property__icon">
                                <UiIcon.Car size={24} color={COLORS._858991} />
                            </div>
                            <div className="vm-entry-pass-modal-property__value">
                                {entryPass.transport}
                            </div>
                        </div>
                        <div className="vm-entry-pass-modal-property">
                            <div className="vm-entry-pass-modal-property__icon">
                                <UiIcon.AlignLeft size={24} color={COLORS._858991} />
                            </div>
                            <div className="vm-entry-pass-modal-property__value">
                                {entryPass.comment}
                            </div>
                        </div>
                    </div>
                    <div
                        className="vm-entry-pass-modal__action"
                        onClick={() => {
                            store.set('isOpened', false);
                            onCopy(entryPass);
                        }}
                    >
                        <UiIcon.Copy size={24} />
                        <span>Дублировать</span>
                    </div>
                </UiModalContent>
            </UiModal>
        </>
    );
});
